<template>
  <!-- <CreatePost v-if="showCreatePost && (isCurrentUserInfluencer || globalStore.currentFeed !== 'influencers')"/> -->

  <div class="feed-tabs-container relative">
    <!-- Left scroll arrow -->
    <div
      v-if="canScrollLeft && !isMobile"
      @click="scrollLeft"
      class="scroll-arrow scroll-arrow-left text-xl"
    >
      <ion-icon
        :icon="chevronBackOutline"
        class="text-black dark:text-white text-xl"
      ></ion-icon>
    </div>

    <div
      ref="scrollableContainerRef"
      class="overflow-x-auto hide-scrollbar w-full"
    >
      <div
        ref="feedTabsRef"
        class="cursor-pointer select-none flex whitespace-nowrap text-md gap-x-4 px-4 py-2 border-gray-500 text-black dark:text-white"
        @scroll="checkScrollable"
      >
        <div
          v-for="feed in visibleFeeds"
          :key="feed.id"
          @click="
            feedType === feed.id ? emit('scroll-top') : emitFeedType(feed.id)
          "
          :class="[
            'pill-tab px-3 rounded-full transition-all flex items-center',
            { 'active-pill': feedType === feed.id && isHome },
          ]"
        >
          <span>{{ feed.name }}</span>
        </div>
        <div>
          <ion-icon
            class="mr-2 mt-2 mb-1 text-lg cursor-pointer select-none text-black dark:text-white"
            :icon="ellipsisHorizontalOutline"
            @click.stop.prevent="openFeedSelectionModal"
          />
        </div>
      </div>
    </div>

    <!-- Right scroll arrow -->
    <div
      v-if="canScrollRight && !isMobile"
      @click="scrollRight"
      class="scroll-arrow scroll-arrow-right"
    >
      <ion-icon
        :icon="chevronForwardOutline"
        class="text-black dark:text-white"
      ></ion-icon>
    </div>
  </div>

  <!-- Feed Selection Modal -->
  <FeedSelectionModal
    :is-open="isFeedSelectionModalOpen"
    :feeds="allFeeds"
    @close="closeFeedSelectionModal"
    @update:feeds="handleFeedsUpdate"
    @feeds-updated="handleFeedsUpdated"
  />
</template>

<script setup>
import { IonIcon } from "@ionic/vue";
import {
  watch,
  computed,
  onBeforeMount,
  ref,
  onMounted,
  onUnmounted,
} from "vue";
import { useAuthStore } from "@/stores/auth";
import { useRoute, useRouter } from "vue-router";
import { useGlobalStore } from "@/stores/global";
import { usePostStore } from "@/stores/post";
import FeedSelectionModal from "./FeedSelectionModal.vue";
import {
  ellipsisHorizontalOutline,
  chevronBackOutline,
  chevronForwardOutline,
} from "ionicons/icons";

defineProps({
  showCreatePost: Boolean,
});

const globalStore = useGlobalStore();
const postStore = usePostStore();
const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const isHome = computed(() => route.path === "/feed");
const isMobile = computed(() => window.innerWidth < 768); // Increased threshold
const feedTabsRef = ref(null);
const scrollableContainerRef = ref(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(false);

const emit = defineEmits(["feedTypeSelected", "scroll-top"]);
const isFeedSelectionModalOpen = ref(false);

// All possible feeds configuration
const allFeeds = ref([
  { id: "forYou", name: "For You", enabled: true, order: 0 },
  {
    id: "following",
    name: "Following",

    enabled: true,
    order: 1,
  },
  {
    id: "influencers",
    name: "Discover",

    enabled: true,
    order: 2,
  },
  { id: "all", name: "Global", enabled: true, order: 3 },
  {
    id: "followingMutual",
    name: "Mutuals",

    enabled: true,
    order: 4,
  },
  {
    id: "groups",
    name: "Groups",
    icon: null,

    enabled: postStore.groupList.myGroups?.length > 0,
    order: 5,
  },
  {
    id: "newUsers",
    name: "Welcome",
    icon: null,

    enabled: true,
    order: 6,
  },
]);

// Initialize from localStorage if available
onBeforeMount(() => {
  if (authStore.currentUser.badges.includes("moderator")) {
    allFeeds.value.push({
      id: "violations",
      name: "Moderation",
      enabled: true,
      order: 7,
    });
  }
  if (postStore.groupList.myGroups.length === 0) {
    postStore.fetchGroupList("myGroups");
  }

  // Load saved feed preferences from localStorage
  const savedFeeds = localStorage.getItem("parlerFeedPreferences");
  if (savedFeeds) {
    try {
      allFeeds.value = JSON.parse(savedFeeds);
    } catch (error) {
      console.error("Error parsing saved feeds:", error);
    }
  }
});

onMounted(() => {
  checkScrollable();

  // Add scroll event listener to update arrow visibility during scrolling
  if (scrollableContainerRef.value) {
    scrollableContainerRef.value.addEventListener("scroll", checkScrollable);
  }

  window.addEventListener("resize", checkScrollable);
});

onUnmounted(() => {
  // Clean up event listeners
  if (scrollableContainerRef.value) {
    scrollableContainerRef.value.removeEventListener("scroll", checkScrollable);
  }
  window.removeEventListener("resize", checkScrollable);
});

// Function to check if the tabs are scrollable and update arrow visibility
function checkScrollable() {
  if (!scrollableContainerRef.value) return;

  const el = scrollableContainerRef.value;

  // Check if scrolling is possible
  const hasHorizontalScroll = el.scrollWidth > el.clientWidth;

  // Update scroll indicators
  canScrollLeft.value = el.scrollLeft > 0;
  canScrollRight.value =
    hasHorizontalScroll && el.scrollLeft < el.scrollWidth - el.clientWidth - 5; // 5px buffer for rounding errors
}

// Scroll functions
function scrollLeft() {
  if (!scrollableContainerRef.value) return;
  scrollableContainerRef.value.scrollBy({ left: -200, behavior: "smooth" });
  // Timeout needed to let the scroll animation finish before checking positions
  setTimeout(checkScrollable, 350);
}

function scrollRight() {
  if (!scrollableContainerRef.value) return;
  scrollableContainerRef.value.scrollBy({ left: 200, behavior: "smooth" });
  // Timeout needed to let the scroll animation finish before checking positions
  setTimeout(checkScrollable, 350);
}

// Get only visible feeds for the header
const visibleFeeds = computed(() => {
  return [...allFeeds.value]
    .filter((feed) => feed.enabled)
    .sort((a, b) => a.order - b.order);
});

// Get default feed (first enabled feed)
const defaultFeed = computed(() => {
  const firstEnabledFeed = visibleFeeds.value[0];
  return firstEnabledFeed ? firstEnabledFeed.id : "forYou";
});

function handleFeedsUpdate(updatedFeeds) {
  allFeeds.value = updatedFeeds;
}

function handleFeedsUpdated() {
  // If the current feed is disabled, switch to the default feed
  if (!visibleFeeds.value.some((feed) => feed.id === feedType.value)) {
    emitFeedType(defaultFeed.value);
  }
}

function openFeedSelectionModal() {
  isFeedSelectionModalOpen.value = true;
}

function closeFeedSelectionModal() {
  isFeedSelectionModalOpen.value = false;
}

const feedType = computed(() => {
  return globalStore.currentFeed;
});

const emitFeedType = (selectedFeedType) => {
  if (route.path !== "/feed") {
    router.push("/feed");
  }

  emit("feedTypeSelected", selectedFeedType);
  globalStore.currentFeed = selectedFeedType;
};

watch(
  () => route.query.feedType,
  (newFeedType) => {
    if (
      newFeedType &&
      visibleFeeds.value.some((feed) => feed.id === newFeedType)
    ) {
      feedType.value = newFeedType;
    } else if (newFeedType) {
      // If the requested feed is not visible, use default
      router.replace({
        path: route.path,
        query: { feedType: defaultFeed.value },
      });
    }
  }
);

// Initialize with default feed if none selected
watch(
  () => visibleFeeds.value,
  (newVisibleFeeds) => {
    if (!feedType.value && newVisibleFeeds.length > 0) {
      emitFeedType(defaultFeed.value);
    }
    // Check scrollable status after feeds update
    setTimeout(checkScrollable, 100);
  },
  { immediate: true }
);
</script>

<style>
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.feed-tabs-container {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 48px; /* Adjust as needed, matches typical tab bar height */
}

.scroll-arrow {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px; /* Adjust width as desired */
  height: 100%; /* Take full height of parent */
  top: 0;
  background-color: rgba(255, 255, 255, 0.9); /* Light background to overlay */
  z-index: 10;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.scroll-arrow:hover {
  background-color: rgba(245, 245, 245, 0.95);
  transform: scale(1.05); /* Adjusted hover effect for square button */
}

.dark .scroll-arrow {
  background-color: rgba(0, 0, 0, 0.65); /* Black background to overlay */
}

.dark .scroll-arrow:hover {
  background-color: rgba(
    10,
    10,
    10,
    0.75
  ); /* Slightly lighter black for hover */
}

.scroll-arrow-left {
  left: 0;
}

.scroll-arrow-right {
  right: 0;
}

.pill-tab {
  background-color: #f3f4f6;
  color: #4b5563;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  opacity: 0.85;
  position: relative;
  z-index: 1;
}

.dark .pill-tab {
  background-color: #09090b;
  color: #d1d5db;
  border: 1px solid #374151;
}

.active-pill {
  background-color: color-mix(
    in srgb,
    var(--primary-color) 50%,
    transparent
  ) !important;
  color: white;
  opacity: 1 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  font-weight: 500;
}

.pill-tab:hover:not(.active-pill) {
  background-color: #e5e7eb;
  transform: translateY(-1px);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.dark .pill-tab:hover:not(.active-pill) {
  background-color: rgba(26, 26, 26, 0.85);
}
</style>
